import { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, X, Sparkles, ExternalLink } from 'lucide-react';
import BrandLogo from '@/components/BrandLogo';

const PERFECT_DIGITALS_URL = 'https://perfectdigitals.co.uk/';

const comparisonRows = [
  {
    traditional: { title: 'Emails & spreadsheets', body: 'Updates and information spread across different channels.' },
    modern: { title: 'One central platform', body: 'Your strategy, communication, work and reporting in one place.' },
  },
  {
    traditional: { title: 'Monthly reporting', body: 'Waiting until the end of the month to understand performance.' },
    modern: { title: 'Ongoing visibility', body: 'See what is happening without waiting for the next monthly report.' },
  },
  {
    traditional: { title: 'Chasing for updates', body: 'Clients often need to ask what is currently being worked on.' },
    modern: { title: 'Clear deliverables', body: 'Understand what is being worked on, what has been completed and what comes next.' },
  },
  {
    traditional: { title: 'Scattered approvals', body: 'Content, campaigns and changes approved across email threads.' },
    modern: { title: 'Centralised approvals', body: 'Review and approve work through the platform.' },
  },
  {
    traditional: { title: 'Limited visibility', body: 'Much of the actual agency workflow happens behind the scenes.' },
    modern: { title: 'A more transparent agency relationship', body: 'Greater visibility into both execution and performance.' },
  },
];

const specialisms = [
  'SEO & Search Visibility',
  'AI Search & AEO',
  'Content Strategy & Creation',
  'Local Search',
  'Paid Advertising',
  'Website & Conversion Optimisation',
  'Product Development',
];

const SectionHeading = ({ eyebrow, children }) => (
  <div className="mb-6">
    {eyebrow && (
      <span className="text-violet-300/80 text-xs font-medium tracking-[0.2em] uppercase">{eyebrow}</span>
    )}
    <h2 className="text-3xl md:text-4xl font-bold mt-3 tracking-tight" style={{ fontFamily: 'Outfit' }}>
      {children}
    </h2>
  </div>
);

export default function AboutUs() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden" data-testid="about-us-page">
      {/* Background glow */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_#1a1033_0%,_#000000_60%)] pointer-events-none" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[500px] bg-violet-600/15 rounded-full blur-[140px] pointer-events-none" />

      {/* Nav */}
      <nav className="relative z-20 max-w-6xl mx-auto px-6 lg:px-8 h-20 flex items-center justify-between">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2"
          data-testid="about-home-logo"
        >
          <BrandLogo size={36} />
          <span className="text-xl font-bold tracking-tight">Law Agency 360</span>
        </button>
        <div className="flex items-center gap-4">
          <Link to="/" className="hidden sm:inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors" data-testid="about-back-home">
            <ArrowLeft className="w-4 h-4" /> Home
          </Link>
          <button
            onClick={() => navigate('/auth')}
            className="px-5 py-2.5 bg-violet-600 hover:bg-violet-500 text-white rounded-xl text-sm font-semibold transition-all hover:shadow-lg hover:shadow-violet-500/25"
            data-testid="about-get-started"
          >
            Get Started
          </button>
        </div>
      </nav>

      <main className="relative z-10 max-w-4xl mx-auto px-6 lg:px-8 pb-24">
        {/* Hero */}
        <header className="pt-10 pb-14">
          <span className="text-violet-300/80 text-xs font-medium tracking-[0.2em] uppercase">Powered by Perfect Digitals</span>
          <h1 className="text-4xl md:text-6xl font-bold mt-4 leading-[1.1] tracking-tight" style={{ fontFamily: 'Outfit' }}>
            About <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-purple-500">LawAgency360</span>
          </h1>
          <div className="space-y-4 text-zinc-300 leading-relaxed mt-8 text-lg">
            <p>
              LawAgency360 is a specialist digital marketing platform for law firms, created by{' '}
              <a href={PERFECT_DIGITALS_URL} target="_blank" rel="noopener noreferrer" className="text-violet-300 hover:text-violet-200 underline underline-offset-2">Perfect Digitals</a>,
              an award-winning digital marketing and AI growth agency operating across Ireland and the UK.
            </p>
            <p>
              Perfect Digitals was built around helping businesses grow through SEO, paid advertising, content, AI visibility, websites and digital strategy. Over time, however, we saw a recurring problem with the traditional agency model: even when the work was being delivered, clients often had limited visibility into what was happening behind the scenes.
            </p>
            <p>
              Updates lived in emails. Approvals were scattered across conversations. Reports arrived once a month. Tasks were managed internally, while the client often saw only the final output.
            </p>
            <p className="text-white font-semibold text-xl">We believed there was a better way to deliver digital marketing.</p>
            <p>
              That thinking led to a more transparent, platform-led model, bringing communication, strategy, deliverables, approvals, reporting and ongoing execution into one place.
            </p>
            <p>
              LawAgency360 takes that model and builds it specifically around the digital growth needs of law firms.
            </p>
          </div>
        </header>

        {/* A Different Kind of Agency */}
        <section className="py-10 border-t border-white/[0.06]">
          <SectionHeading eyebrow="A different kind of agency">A Different Kind of Digital Marketing Agency</SectionHeading>
          <div className="space-y-4 text-zinc-300 leading-relaxed">
            <p>
              Traditional agency relationships can involve countless emails, spreadsheets, meetings and monthly reports simply to understand what is happening with your marketing.
            </p>
            <p>LawAgency360 replaces much of that fragmentation with one central platform.</p>
          </div>

          {/* Comparison table */}
          <div className="mt-8">
            <h3 className="text-lg font-semibold text-white mb-4">The Traditional Model vs The LawAgency360 Model</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Traditional column header */}
              <div className="hidden md:flex items-center gap-2 px-5 py-3 rounded-xl bg-white/[0.03] border border-white/10">
                <X className="w-4 h-4 text-rose-400" />
                <span className="font-semibold text-zinc-300">The Traditional Model</span>
              </div>
              <div className="hidden md:flex items-center gap-2 px-5 py-3 rounded-xl bg-violet-500/10 border border-violet-500/30">
                <Check className="w-4 h-4 text-violet-300" />
                <span className="font-semibold text-white">The LawAgency360 Model</span>
              </div>

              {comparisonRows.map((row) => (
                <div key={row.traditional.title} className="contents">
                  <div className="rounded-xl bg-white/[0.02] border border-white/10 p-5" data-testid={`compare-traditional-${row.traditional.title}`}>
                    <div className="flex items-start gap-2">
                      <X className="w-4 h-4 text-rose-400 mt-1 flex-shrink-0 md:hidden" />
                      <div>
                        <div className="font-semibold text-zinc-200">{row.traditional.title}</div>
                        <p className="text-sm text-zinc-400 mt-1">{row.traditional.body}</p>
                      </div>
                    </div>
                  </div>
                  <div className="rounded-xl bg-gradient-to-b from-violet-600/10 to-transparent border border-violet-500/30 p-5" data-testid={`compare-modern-${row.modern.title}`}>
                    <div className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-violet-300 mt-1 flex-shrink-0" />
                      <div>
                        <div className="font-semibold text-white">{row.modern.title}</div>
                        <p className="text-sm text-zinc-400 mt-1">{row.modern.body}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Technology-led. Human-delivered. */}
        <section className="py-10 border-t border-white/[0.06]">
          <SectionHeading eyebrow="How we work">Technology-led. Human-delivered.</SectionHeading>
          <div className="space-y-4 text-zinc-300 leading-relaxed">
            <p>LawAgency360 isn't DIY marketing software.</p>
            <p>
              We don't give your firm a dashboard and expect you to manage your own SEO, write your own content or optimise your advertising.
            </p>
            <p>
              Real digital marketing specialists do the work. The technology simply gives you a better way to manage and see it.
            </p>
            <p>Behind the platform, specialists work across:</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-5">
            {specialisms.map((s) => (
              <div key={s} className="flex items-center gap-3 px-4 py-3 rounded-xl bg-white/[0.03] border border-white/10">
                <div className="w-8 h-8 rounded-lg bg-violet-500/15 flex items-center justify-center flex-shrink-0">
                  <Check className="w-4 h-4 text-violet-300" />
                </div>
                <span className="text-zinc-200 font-medium">{s}</span>
              </div>
            ))}
          </div>
          <div className="space-y-4 text-zinc-300 leading-relaxed mt-6">
            <p>
              The platform connects your law firm with the people doing the work - keeping strategy, execution, communication, approvals and reporting together.
            </p>
            <p className="text-white font-semibold text-xl">The expertise of an agency. The visibility of a platform.</p>
          </div>
        </section>

        {/* Proven Experience */}
        <section className="py-10 border-t border-white/[0.06]">
          <SectionHeading eyebrow="Track record">Proven Experience Behind LawAgency360</SectionHeading>
          <div className="space-y-4 text-zinc-300 leading-relaxed">
            <p>
              The experience behind LawAgency360 comes from Perfect Digitals, where our team has worked with businesses across a wide range of industries, markets and digital challenges.
            </p>
            <p>
              From SEO and organic growth to paid advertising, content, AI visibility and digital strategy, that experience has helped shape the systems, processes and expertise that now power LawAgency360.
            </p>
            <p>
              Our wider portfolio of work and results is available through Perfect Digitals, including detailed case studies across different industries and services.
            </p>
          </div>
          <a
            href={PERFECT_DIGITALS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 mt-6 px-5 py-3 rounded-xl border border-violet-500/40 bg-violet-500/10 text-violet-200 hover:bg-violet-500/20 transition-all font-medium"
            data-testid="about-case-studies-link"
          >
            Explore Perfect Digitals Case Studies <ExternalLink className="w-4 h-4" />
          </a>
        </section>

        {/* Why Law Firms */}
        <section className="py-10 border-t border-white/[0.06]">
          <SectionHeading eyebrow="Focus">Why Law Firms?</SectionHeading>
          <div className="space-y-4 text-zinc-300 leading-relaxed">
            <p>We could have simply added legal marketing as another service within a general digital marketing agency.</p>
            <p>Instead, we chose to build something specifically for the sector.</p>
            <p>
              Law firms compete in some of the most valuable and competitive search markets. Growth can depend on visibility across individual practice areas, locations, Google search, local results, paid acquisition and, increasingly, AI-driven search experiences.
            </p>
            <p>
              At the same time, firms need greater clarity over what their marketing partner is actually doing and how that work contributes to growth.
            </p>
            <p>LawAgency360 brings those requirements together.</p>
            <p className="text-white font-medium">
              A specialist marketing model for law firms, delivered by experienced digital marketers and managed through one platform.
            </p>
          </div>
        </section>

        {/* Built for transparency */}
        <section className="py-10 border-t border-white/[0.06]">
          <SectionHeading eyebrow="Our belief">Built for a More Transparent Agency Relationship</SectionHeading>
          <div className="space-y-4 text-zinc-300 leading-relaxed">
            <p>We believe law firms should be able to see more than a monthly report.</p>
            <p>
              They should be able to understand what is being worked on, communicate with the people delivering it, review work, track progress and see how their digital presence is evolving.
            </p>
            <p className="text-white font-semibold text-xl">That is what LawAgency360 was built to provide.</p>
          </div>
        </section>

        {/* Closing CTA */}
        <section className="mt-6">
          <div className="relative rounded-3xl border border-violet-500/30 bg-gradient-to-br from-violet-600/15 via-slate-900 to-slate-950 p-10 text-center overflow-hidden">
            <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-[400px] h-[300px] bg-violet-600/25 rounded-full blur-[120px] pointer-events-none" />
            <div className="relative">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/5 border border-white/10 mb-5">
                <Sparkles className="w-4 h-4 text-violet-300" />
                <span className="text-sm text-violet-200">Free Growth Snapshot</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Outfit' }}>
                See where your law firm can grow.
              </h2>
              <p className="text-zinc-300 mt-4 max-w-xl mx-auto">
                Start with your free Growth Snapshot and discover opportunities across search, AI visibility and digital growth.
              </p>
              <button
                onClick={() => navigate('/auth')}
                className="inline-flex items-center gap-2 mt-8 px-7 py-3.5 bg-violet-600 hover:bg-violet-500 text-white rounded-xl font-semibold transition-all hover:shadow-lg hover:shadow-violet-500/30"
                data-testid="about-growth-snapshot-cta"
              >
                Start Your Free Growth Snapshot <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </section>
      </main>

      <footer className="relative z-10 py-10 px-6 lg:px-8 border-t border-white/5 text-center text-xs text-zinc-500">
        <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 mb-2">
          <Link to="/" className="hover:text-violet-300 transition-colors">Home</Link>
          <Link to="/privacy-policy" className="hover:text-violet-300 transition-colors">Privacy Policy</Link>
          <Link to="/terms" className="hover:text-violet-300 transition-colors">Terms &amp; Conditions</Link>
        </div>
        &copy; 2026 Law Agency 360. All rights reserved.
      </footer>
    </div>
  );
}
